// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 4 miest
// PredajnaCP — Cenové ponuky v Predajni (Mirko 25.7. „Predajňa nemá CP").
// Katalóg položiek z PLU cenníka (3 hladiny + vlastná marža + sklad A + nákupná).
//
// 🔴 ROVNAKÝ DIZAJN AKO STAVEBNÁ CP (Mirko 2026-07-27, ÚSTAVNÉ: „prečo neurobíš rovnaký dizajn
//    — to je príkaz ústava", „panel o vstupných údajov musia byť v Matrix rovnaké").
//    Preto: zdieľané `FieldGrid`/`Field`/`inp` (components/DocFields.jsx) + zdieľaný `StatusTag`,
//    brand rám, badge verzie, zámok odoslaného, hľadanie + filtre + KPI v zozname,
//    zákazník cez `SearchableSelect` (aj podľa IČO), položky ako tabuľka so zebrou a hlavičkou,
//    odosielanie cez zdieľaný `SendCustomerDialog` (ústava 103.E).
//    ZÁMERNE BEZ (Mirko): „Obhliadku vykonal" a „Príplatky k cene diela".
//    S internou poznámkou (do PDF nejde).
//
// 🔴 OPRAVY 2026-07-28 (Mirko: „nefunguje uložiť a editovať, nenašlo PLU 281, 2467 tiež nejde"):
//    1. PLU ponuka sa OREZÁVALA — bola `position:absolute` vnútri karty položiek s `overflow:hidden`.
//       Teraz `position:fixed` ukotvená na rect inputu → žiadny rodič ju neoreže.
//    2. Po uložení sa volalo `openDetail({ id })` (objekt len s id) → editor si prepísal hlavičku
//       prázdnymi hodnotami a vyzeralo to, že sa nedá editovať. Teraz sa načíta ČERSTVÝ riadok.
//    3. Po vytvorení sa editor zavieral (`setEd(null)`) — teraz ostáva otvorený na novej CP.
//    4. Hladina ceny / marža / nákupná sa NEUKLADALI → po znovuotvorení sa nedalo prepnúť hladinu.
//       Teraz sa ukladajú (`plu_hladina`, `marza_pct`, `nakup_cena`) a ceny sa dohydratujú z cenníka.
//    5. ÚSTAVNÉ (Mirko 28.7.): „všade uvádzaj, že ceny zadáva používateľ sú BEZ DPH."
//    6. NOVÉ: 4. cenová kategória „Vlastná %" — zadáš len % marže, cena/MJ = nákup × (1 + %/100).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import TelefonInput from '../components/TelefonInput.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { setDirty as setGuardDirty, isAnyDirty, pripravDokument, otvorDokument } from '../hooks/unsavedGuard.js'   // p97 — doklad = obrazovka (ústava 67.15)
import { useAuth } from '../auth/AuthContext.jsx'
import { COLORS, TYPO, SPACE, RADIUS, TINT, todayLocal, hladaj, SK } from '../theme.js'   // CAS_ODOSLANIA_V1
import NoviCustomerDialog from '../components/NoviCustomerDialog.jsx'
import SendCustomerDialog from '../components/SendCustomerDialog.jsx'
import { Field, FieldGrid, inp } from '../components/DocFields.jsx'
import StatusTag, { STATUS_LABEL } from '../components/StatusTag.jsx'
import { SearchInput, FilterChips, KpiRow, KpiCard, SearchableSelect, ListRow, ListStatusBar, Tabs } from '../components/Ui.jsx'
import { HistoryPanel } from '../components/HistoryPanel.jsx'
import RowMenu from '../components/RowMenu.jsx'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { Btn, BtnAdd } from '../components/Btn.jsx'
import { usePinGate } from '../hooks/usePinGate.jsx'   // cp_zmaz_v1: odoslanú verziu CP odloží až PIN (ústava 63)

const eur = (x) => (Number(x) || 0).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const n2 = (x) => Math.round((Number(x) || 0) * 100) / 100
const TODAY = () => todayLocal()
const normPhone = (s) => String(s || '').replace(/[^\d+]/g, '')
// DPH: 0 % je LEGITÍMNA hodnota — `Number(x) || 23` ju tichu prepísalo na 23 (chyba nájdená auditom 28.7.)
const numOr = (v, def) => { const s = String(v ?? '').trim(); if (s === '') return def; const n = Number(s); return Number.isFinite(n) && n >= 0 ? n : def }
const vatOut = (v) => { const s = String(v ?? '').trim(); if (s === '') return 23; const n = Number(s); return Number.isFinite(n) && n >= 0 ? n : 23 }

// Hladiny podľa MKsoftu — ceny BEZ DPH. Mirko: „ja si vyberiem cenu podľa zákazníka", žiadny auto-default.
// 4. kategória (Mirko 28.7.): „vlastná cenová kategória, kde zadám iba % marže" → z nákupnej ceny.
const HLADINY = [
  { key: 'c1_bez', label: 'MO' },
  { key: 'c2_bez', label: 'VO' },
  { key: 'c3_bez', label: 'Eshop' },
]
const MARZA_KEY = 'marza'

// cena bez DPH z nákupnej ceny a marže v % (Mirko 28.7.)
const zMarze = (nakup, pct) => n2((Number(nakup) || 0) * (1 + (Number(pct) || 0) / 100))

// Rovnake farby ako stavebna CP (STATUS_FILTERS v CenoveView) — vizualna zhoda (Mirko 27.7.).
const FILTERS = [
  { key: 'all',      label: 'Všetko',      color: COLORS.muted },
  { key: 'draft',    label: 'Pracovné',    color: COLORS.muted },
  { key: 'sent',     label: 'Odoslané',    color: COLORS.nav },
  { key: 'accepted', label: 'Akceptované', color: COLORS.add },
  { key: 'rejected', label: 'Zamietnuté',  color: COLORS.remove },
]

// Vyhľadávanie zákazníka MUSÍ nájsť aj podľa IČO — placeholder to sľubuje (Mirko 27.7.).
// SearchableSelect filtruje podľa `label`, takže IČO musí byť v ňom.
// V zozname aj v hlavicke editora sa hlada podla FIRMY, nie podla cisla (Mirko 28.7.).
const zakLabel = (q) => q.customer_name || q.custName || '— bez zákazníka —'
const custLabel = (c) => c.name + (c.ico ? ' · IČO ' + c.ico : '') + ' (' + c.id + ')'

// Rozloženie stĺpcov tabuľky položiek — hlavička aj riadky používajú TO ISTÉ.
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: '1fr 196px 64px 84px 104px 92px 76px',
  gap: 4, padding: '6px 10px',
}

const freshLine = () => ({ _k: Math.random().toString(36).slice(2), id: null, plu: '', name: '', unit: 'ks', qty: 1, price: 0, nakup: 0, hladina: '', marza: '', prices: null, sklad: null })

// ── PLU picker: hľadá v /api/plu-cennik/search, napĺňa názov/MJ/ceny/sklad ──
// Ponuka je `position: fixed` — karta položiek má `overflow:hidden` a orezávala by ju (chyba 28.7.).
function PluCell({ line, canBuy, onPick, onName }) {
  const [q, setQ] = useState('')
  const [res, setRes] = useState([])
  const [open, setOpen] = useState(false)
  const [stav, setStav] = useState('')   // '', 'hladam', 'prazdne', 'chyba' — tichý neúspech vyzeral ako rozbité (audit 28.7.)
  const [rect, setRect] = useState(null)
  const box = useRef(null)
  const inputRef = useRef(null)

  const measure = useCallback(() => {
    if (!inputRef.current) return
    const r = inputRef.current.getBoundingClientRect()
    setRect({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 420) })
  }, [])

  useEffect(() => {
    if (!open) return
    const term = q.trim()
    if (term.length < 2) { setRes([]); setStav(''); return }
    setStav('hladam')
    const t = setTimeout(async () => {
      try {
        const r = await fetchAuth('/api/plu-cennik/search?q=' + encodeURIComponent(term))
        if (!r.ok) { setRes([]); setStav(r.status === 403 ? 'chyba403' : 'chyba'); measure(); return }
        const rows = await r.json()
        const arr = Array.isArray(rows) ? rows : []
        setRes(arr); setStav(arr.length ? '' : 'prazdne')
        measure()
      } catch (_) { setRes([]); setStav('chyba'); measure() }
    }, 220)
    return () => clearTimeout(t)
  }, [q, open, measure])

  useEffect(() => {
    if (!open) return
    const h = () => measure()
    window.addEventListener('scroll', h, true); window.addEventListener('resize', h)
    return () => { window.removeEventListener('scroll', h, true); window.removeEventListener('resize', h) }
  }, [open, measure])

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h)
  }, [])

  return (
    <div ref={box} style={{ position: 'relative', minWidth: 0 }}>
      <input
        ref={inputRef}
        value={line.name}
        placeholder="názov alebo číslo PLU (min. 2 znaky)…"
        onChange={e => { onName(e.target.value); setQ(e.target.value); setOpen(true); measure() }}
        onFocus={() => { if (!line.plu) { setQ(line.name); setOpen(true); measure() } }}
        style={{ ...inp, fontWeight: TYPO.weights.medium }} />
      {line.plu ? <div style={{ fontSize: 10.5, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 1 }}>PLU {line.plu}{line.sklad != null ? ' · sklad A: ' + line.sklad : ''}{canBuy && line.nakup ? ' · nákup ' + eur(line.nakup) + ' bez DPH' : ''}</div> : null}
      {open && rect && (res.length > 0 || stav) && (
        <div style={{ position: 'fixed', zIndex: 4000, top: rect.top, left: rect.left, width: rect.width, maxHeight: 300, overflowY: 'auto', background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, boxShadow: '0 10px 28px rgba(0,0,0,0.18)' }}>
          <div style={{ padding: '4px 9px', fontSize: 10.5, color: COLORS.muted, borderBottom: `1px solid ${COLORS.line}`, background: COLORS.bgSoft }}>
            {stav === 'hladam' ? 'Hľadám v cenníku…'
              : stav === 'prazdne' ? 'Žiadna zhoda v PLU cenníku — názov môžeš napísať ručne.'
              : stav === 'chyba403' ? 'Na PLU cenník nemáš oprávnenie (rola).'
              : stav === 'chyba' ? 'Cenník sa nepodarilo načítať — skús znova.'
              : res.length + ' nájdených · všetky ceny sú BEZ DPH'}
          </div>
          {res.map(r => (
            <button key={r.plu} type="button" onClick={() => { onPick(r); setOpen(false); setRes([]); setStav('') }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 9px', border: 'none', borderBottom: `1px solid ${COLORS.line}`, background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
              <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.medium, color: COLORS.text }}>{r.name}</div>
              <div style={{ fontSize: 10.5, color: COLORS.muted, fontFamily: TYPO.mono }}>
                PLU {r.plu} · {r.mj || 'ks'} · MO {eur(r.c1_bez)} / VO {eur(r.c2_bez)} / Eshop {eur(r.c3_bez)} (bez DPH) · sklad A {r.sklad_a ?? 0}{canBuy ? ' · nákup ' + eur(r.cena_nakup) : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function PredajnaCP() {
  const toast = useToast()
  const pinGate = usePinGate()   // cp_zmaz_v1
  const { user } = useAuth()
  const canBuy = ['admin', 'office_manager', 'owner', 'owner_view', 'service'].includes(user?.role)  // nákupná cena/marža — Marek (service) smie
  const [list, setList] = useState([])
  const [customers, setCustomers] = useState([])
  const [selId, setSelId] = useState(null)
  const [ed, setEd] = useState(null)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [newCustomerOpen, setNewCustomerOpen] = useState(false)
  const [newCustomerPrefill, setNewCustomerPrefill] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [mailFor, setMailFor] = useState(null)
  const [tab, setTab] = useState('detail')          // Detail | História (ústava 12.0/1)
  const [baseline, setBaseline] = useState('')      // odtlačok načítaného stavu — ochrana neuložených zmien

  // vracia riadky, aby si volajúci vedel vytiahnuť ČERSTVÝ riadok (oprava 28.7.)
  const load = useCallback(async () => {
    try {
      const r = await fetchAuth('/api/quotes')
      const all = r.ok ? await r.json() : []
      const rows = (Array.isArray(all) ? all : []).filter(q => q.druh === 'predajna' && !q.replaced_by)
      setList(rows)
      return rows
    } catch (_) { return [] }
  }, [])
  useEffect(() => { load() }, [load])
  const loadCustomers = useCallback(() => {
    fetchAuth('/api/customers').then(r => r.ok ? r.json() : []).then(d => setCustomers(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])
  useEffect(() => { loadCustomers() }, [loadCustomers])

  // ── filter + fulltext (rovnako ako stavebná CP, ústava 56) ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return list.filter(row => {
      if (filter !== 'all' && row.status !== filter) return false
      if (!q) return true
      return Object.values(row).some(v => v != null && hladaj(String(v), q))
    })
  }, [list, search, filter])

  const kpi = useMemo(() => ({
    total: list.length,
    sent: list.filter(r => r.status === 'sent').length,
  }), [list])

  // Klavesova navigacia zoznamu — ROVNAKO ako CP/OBJ/Obhliadky (ustava 54.2; Mirko 27.7. „lavy stlpec ma takto fungovat vsade")
  useListKeyboard({
    items: filtered,
    selectedId: selId,
    onSelect: (id) => { const q = filtered.find(x => x.id === id); if (q) openDetail(q) },
    onOpen: (q) => openDetail(q),
    enabled: !newCustomerOpen && !mailFor,
  })

  // ── OCHRANA NEULOŽENÝCH ZMIEN (Mirko GO 28.7.) ────────────────────────────
  // Odtlačok stavu editora. Porovnaním s `baseline` vieme, či je čo stratiť.
  const snap = (e) => e ? JSON.stringify({
    h: [e.customerId, e.stavba, e.date_issued, e.validity_days, e.status, e.prepared_by,
        e.vat_rate, e.doc_phone, e.contact_email, e.contact_email2, e.contact_phone,
        e.internal_note, e.custom_text, e.hide_total],
    l: (e.lines || []).map(l => [l.plu, l.name, l.unit, l.qty, l.price, l.hladina, l.marza]),
  }) : ''
  const dirty = !!ed && snap(ed) !== baseline
  // Vráti true = smieš pokračovať. Pri rozrobenej ponuke sa opýta.
  const smiemOdist = () => !dirty || (!docLocked && !isAnyDirty()) || window.confirm(   // straz_v3: keď spoločná stráž už rozhodla (Uložiť / Zahodiť), druhýkrát sa nepýta
    'V rozpracovanej ponuke máš neuložené zmeny.\n\nZahodiť ich a pokračovať?')

  // Zatvorenie/obnovenie okna s rozrobenou ponukou — prehliadač sa opýta sám.
  useEffect(() => {
    if (!dirty) return
    const h = (ev) => { ev.preventDefault(); ev.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [dirty])

  const blank = () => ({
    customerId: '', custName: '', stavba: '', quote_number: '', date_issued: TODAY(),
    validity_days: 14, status: 'draft', prepared_by: 'Kašubjak', vat_rate: 23,
    contact: '+421 918 443 675', contact_email: '', contact_email2: '', contact_phone: '',
    internal_note: '', custom_text: '', hide_total: 0, doc_version: 1, sent_at: null, updated_at: '', lines: [freshLine()],
  })

  const startNew = () => {
    if (!smiemOdist()) return
    const b = blank()
    setSelId(null); setIsNew(true); setTab('detail'); setEd(b); setBaseline(snap(b))
  }

  // Po znovuotvorení CP treba k položkám dotiahnuť aktuálne cenníkové hladiny,
  // inak by sa nedalo prepnúť MO/VO/Eshop (chyba 28.7.).
  const hydratePrices = async (lines) => {
    const withPlu = lines.filter(l => l.plu)
    if (!withPlu.length) return lines
    const map = {}
    await Promise.all(withPlu.map(async l => {
      try {
        const r = await fetchAuth('/api/plu-cennik/search?q=' + encodeURIComponent(String(l.plu).replace(/\.0+$/, '')))
        if (!r.ok) return
        const rows = await r.json()
        const hit = (Array.isArray(rows) ? rows : []).find(x => String(x.plu) === String(l.plu))
        if (hit) map[l.plu] = hit
      } catch (_) {}
    }))
    return lines.map(l => {
      const h = map[l.plu]
      if (!h) return l
      return {
        ...l,
        sklad: h.sklad_a ?? 0,
        nakup: l.nakup || Number(h.cena_nakup) || 0,
        prices: { c1_bez: Number(h.c1_bez) || 0, c2_bez: Number(h.c2_bez) || 0, c3_bez: Number(h.c3_bez) || 0 },
      }
    })
  }

  const openDetail = async (q, preskocitStraz) => {
    if (!preskocitStraz && !smiemOdist()) return
    setSelId(q.id); setIsNew(false); setTab('detail'); setLoadingDetail(true)
    try {
      const r = await fetchAuth('/api/quotes/' + q.id + '/lines')
      const rows = r.ok ? await r.json() : []
      let lines = (Array.isArray(rows) ? rows : []).map(l => ({
        _k: 'l' + l.id, id: l.id, plu: l.plu || '', name: l.title || '', unit: l.unit || 'ks',
        qty: l.qty, price: l.price, nakup: Number(l.nakup_cena) || 0,
        hladina: l.plu_hladina || '', marza: l.marza_pct ? String(l.marza_pct) : '',
        prices: null, sklad: null,
      }))
      lines = await hydratePrices(lines)
      const _ed = {
        customerId: q.customerId || q.customer_id || '', custName: q.customer_name || '', stavba: q.stavba || '',
        quote_number: q.quote_number || '', date_issued: String(q.date_issued || '').slice(0, 10) || TODAY(),
        validity_days: q.validity_days || 14, status: q.status || 'draft', prepared_by: q.prepared_by || '',
        vat_rate: q.vat_rate ?? 23, doc_phone: q.doc_phone || '',
        contact_email: q.contact_email || '', contact_email2: q.contact_email2 || '', contact_phone: q.contact_phone || '',
        internal_note: q.internal_note || '', custom_text: q.custom_text || '',
        hide_total: Number(q.hide_total) === 1 ? 1 : 0,
        doc_version: Number(q.doc_version || 1), sent_at: q.sent_at || null,
        updated_at: q.updated_at || '',
        lines: lines.length ? lines : [freshLine()],
      }
      setEd(_ed); setBaseline(snap(_ed))
    } catch (e) { toast.error('Nepodarilo sa načítať detail: ' + e.message) }
    finally { setLoadingDetail(false) }
  }

  // ⧉ DUPLIKÁT (ústava 57 r.3276 — „Duplikovať" vo VŠETKÝCH zoznamoch; predajna_dup_v1, 29. 9. 2026)
  // Mirko 29. 9. 2026: „zákazníka neduplikovať iba položky" — server prenesie LEN položky,
  // zákazník, stavba a kontakty ostanú prázdne. Nová CP sa hneď otvorí na doplnenie zákazníka.
  const duplikovat = async (q) => {
    if (!smiemOdist()) return
    if (!window.confirm('Vytvoriť kópiu ponuky ' + (q.quote_number || q.id) + '?\n\n'
      + 'Prenesú sa LEN položky — dostane nové číslo a dnešný dátum.\n'
      + 'Zákazníka a kontakt doplníš nanovo.')) return
    try {
      const r = await fetchAuth('/api/cp/' + q.id + '/copy', { method: 'POST' })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status)
      toast.success('Kópia vytvorená: ' + (d.id || '') + ' — doplň zákazníka.')
      await reopen(d.id)
    } catch (e) { toast.error('Kópiu sa nepodarilo vytvoriť: ' + e.message) }
  }

  // znovuotvorenie z ČERSTVÝCH dát servera (nie z útržku {id}) — oprava 28.7.
  const reopen = async (id) => {
    const rows = await load()
    const fresh = rows.find(r => r.id === id)
    if (fresh) await openDetail(fresh, true)   // po uložení niet čo strážiť
  }

  const setF = (k, v) => setEd(e => ({ ...e, [k]: v }))
  const docLocked = !!(ed && ed.sent_at)
  // p97: predajňová CP sa hlási do spoločného registra neuložených zmien — PDF, tlač a okno Odoslať ju najprv uložia (ústava 67.15)
  useEffect(() => {
    const k = 'predajna-cp'
    setGuardDirty(k, !!ed && dirty && !docLocked, () => save(), () => { setEd(null); setIsNew(false); setSelId(null); setBaseline('') })   // straz_v3: „Zahodiť zmeny“ zavrie rozpracovanú ponuku
    return () => setGuardDirty(k, false)
  })

  // Kontakt z karty partnera — doplní LEN ak je pole prázdne (nedeštruktívne, ako CP/Obhliadky).
  const pickCustomer = (v) => {
    setF('customerId', v)
    const cc = customers.find(x => x.id === v)
    if (!cc) return
    setEd(e => ({
      ...e, customerId: v, custName: cc.name || e.custName,
      contact_email: String(e.contact_email || '').trim() || cc.email || '',
      contact_email2: String(e.contact_email2 || '').trim() || cc.email2 || '',
      contact_phone: String(e.contact_phone || '').trim() || cc.phone || cc.contact || '',
    }))
  }

  const setLine = (k, patch) => setEd(e => ({ ...e, lines: e.lines.map(l => l._k === k ? { ...l, ...patch } : l) }))
  const addLine = () => setEd(e => ({ ...e, lines: [...e.lines, freshLine()] }))
  const delLine = (k) => setEd(e => ({ ...e, lines: e.lines.filter(l => l._k !== k) }))
  // Poradie položiek (Mirko GO 28.7.) — mení sa lokálne, poradie sa zapíše pri uložení
  // ako `sort_order` (PDF číta ORDER BY sort_order). Funguje aj pri neuloženej ponuke.
  const moveLine = (k, smer) => setEd(e => {
    const i = e.lines.findIndex(l => l._k === k)
    const j = i + smer
    if (i < 0 || j < 0 || j >= e.lines.length) return e
    const ls = [...e.lines]
    ls[i] = e.lines[j]; ls[j] = e.lines[i]
    return { ...e, lines: ls }
  })
  const pickPlu = (k, r) => setLine(k, {
    plu: r.plu, name: r.name, unit: r.mj || 'ks', nakup: Number(r.cena_nakup) || 0, sklad: r.sklad_a ?? 0,
    prices: { c1_bez: Number(r.c1_bez) || 0, c2_bez: Number(r.c2_bez) || 0, c3_bez: Number(r.c3_bez) || 0 },
    hladina: '', marza: '', price: 0,
  })
  const pickHladina = (k, hk) => {
    const l = ed.lines.find(x => x._k === k)
    if (!l) return
    if (hk === MARZA_KEY) {
      const m = l.marza || ''
      setLine(k, { hladina: MARZA_KEY, price: m === '' ? l.price : zMarze(l.nakup, m) })
      return
    }
    setLine(k, { hladina: hk, marza: '', price: (l.prices || {})[hk] || 0 })
  }
  const setMarza = (k, v) => {
    const l = ed.lines.find(x => x._k === k)
    if (!l) return
    setLine(k, { marza: v, hladina: MARZA_KEY, price: v === '' ? l.price : zMarze(l.nakup, v) })
  }

  const lineSum = (l) => n2((Number(l.qty) || 0) * (Number(l.price) || 0))
  const bez = ed ? n2(ed.lines.reduce((s, l) => s + lineSum(l), 0)) : 0
  const vatPct = ed ? (Number(ed.vat_rate) || 0) : 23
  const dph = n2(bez * vatPct / 100)

  const linePayload = (l, i) => ({
    title: l.name, full_name: '', unit: l.unit || 'ks',   // PLU je INTERNY udaj — na doklad pre zakaznika nepatri (audit 28.7.)
    qty: Number(l.qty) || 0, price: Number(l.price) || 0, plu: l.plu || '',
    plu_hladina: l.hladina || '', marza_pct: Number(l.marza) || 0, nakup_cena: Number(l.nakup) || 0,
    price_mode: 'combined', sort_order: i + 1,
  })

  const save = async () => {
    if (!ed.customerId) { toast.error('Zákazník je povinný — vyber alebo založ nového.'); return false }
    const ls = ed.lines.filter(l => (l.name || '').trim())
    if (!ls.length) { toast.error('Pridaj aspoň jednu položku s názvom.'); return false }
    // Ponuka za 0 € odchádza zákazníkovi ako platná — pýtaj sa (audit 28.7.).
    const nulove = ls.filter(l => !(Number(l.price) > 0))
    if (nulove.length && !window.confirm(
      'Tieto položky majú cenu 0 €:\n' + nulove.map(l => ' · ' + l.name).join('\n') +
      '\n\nUložiť ponuku aj tak? (V PDF sa vytlačia za 0,00 €.)')) return false
    setSaving(true)
    try {
      const head = {
        customerId: ed.customerId, stavba: ed.stavba || '', date_issued: ed.date_issued || TODAY(),
        validity_days: numOr(ed.validity_days, 14), prepared_by: ed.prepared_by || 'Kašubjak',
        doc_phone: ed.doc_phone || '', inspected_by: '',
        contact_email: ed.contact_email || '', contact_email2: ed.contact_email2 || '', contact_phone: ed.contact_phone || '',
        internal_note: ed.internal_note || '', custom_text: ed.custom_text || '',
        hide_total: Number(ed.hide_total) === 1 ? 1 : 0,   // 11.9.2026 (bod 92): prepínač sumy aj v Predajni (PDF ho rešpektuje)
      }
      if (isNew) {
        const r = await fetchAuth('/api/quotes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...head, druh: 'predajna' }) })
        if (!r.ok) { const e2 = await r.json().catch(() => ({})); throw new Error(e2.error || 'HTTP ' + r.status) }
        const q = await r.json()
        for (let i = 0; i < ls.length; i++) {
          const rr = await fetchAuth('/api/quotes/' + q.id + '/lines', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(linePayload(ls[i], i)) })
          if (!rr.ok) { const e3 = await rr.json().catch(() => ({})); throw new Error(e3.error || 'položka sa neuložila (HTTP ' + rr.status + ')') }
        }
        const pr = await fetchAuth('/api/quotes/' + q.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vop_override: '[]', vat_rate: vatOut(ed.vat_rate), status: ed.status || 'draft', notes: 'Predajňová cenová ponuka', hide_total: Number(ed.hide_total) === 1 ? 1 : 0 }) })
        if (!pr.ok) { const e4 = await pr.json().catch(() => ({})); throw new Error(e4.error || 'hlavička sa neuložila (HTTP ' + pr.status + ')') }
        toast.success('Predajňová CP ' + (q.quote_number || q.id) + ' vytvorená.')
        setIsNew(false)
        await reopen(q.id)   // editor ostáva OTVORENÝ na novej CP (oprava 28.7.)
        return true
      } else {
        const hr = await fetchAuth('/api/quotes/' + selId, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          // updated_at = poistka proti dvom ľuďom naraz (Mirko GO 28.7.): backend porovná
          // a pri cudzej medzizmene vráti 409 namiesto tichého prepísania.
          body: JSON.stringify({ ...head, vat_rate: vatOut(ed.vat_rate), status: ed.status || 'draft', updated_at: ed.updated_at || undefined }) })
        if (hr.status === 409) {
          setSaving(false)
          if (window.confirm('Túto ponuku medzitým zmenil niekto iný.\n\nTvoje zmeny sa NEULOŽILI, aby neprepísali jeho prácu.\n\nNačítať aktuálnu verziu zo servera? (tvoje zmeny sa stratia)')) {
            await reopen(selId)
          }
          return false
        }
        if (!hr.ok) { const e2 = await hr.json().catch(() => ({})); throw new Error(e2.error || 'HTTP ' + hr.status) }
        const origR = await fetchAuth('/api/quotes/' + selId + '/lines')
        const orig = origR.ok ? await origR.json() : []
        const keepIds = new Set(ls.filter(l => l.id).map(l => l.id))
        for (const o of (Array.isArray(orig) ? orig : [])) {
          if (!keepIds.has(o.id)) {
            const dr = await fetchAuth('/api/quotes/' + selId + '/lines/' + o.id, { method: 'DELETE' })
            if (!dr.ok) { const e5 = await dr.json().catch(() => ({})); throw new Error(e5.error || 'položku sa nepodarilo zmazať (HTTP ' + dr.status + ')') }
          }
        }
        for (let i = 0; i < ls.length; i++) {
          const l = ls[i]
          const payload = linePayload(l, i)
          const rr = l.id
            ? await fetchAuth('/api/quotes/' + selId + '/lines/' + l.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
            : await fetchAuth('/api/quotes/' + selId + '/lines', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
          if (!rr.ok) { const e3 = await rr.json().catch(() => ({})); throw new Error(e3.error || 'položka „' + l.name + '" sa neuložila') }
        }
        toast.success('Zmeny uložené.')
        await reopen(selId)   // čerstvý riadok zo servera (oprava 28.7.)
        return true
      }
    } catch (e) { toast.error('Nepodarilo sa uložiť: ' + e.message); return false }
    finally { setSaving(false) }
  }

  const delCp = async (id, label) => {
    if (!window.confirm('Zmazať predajňovú CP ' + label + '?')) return
    try {
      let r = await fetchAuth('/api/quotes/' + id, { method: 'DELETE' })
      // cp_zmaz_v1 (8. 10. 2026): odoslanú verziu CP odloží až schválenie PIN-om (ústava 63) — server si ho vyžiada.
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        if (!d.pin_potrebny) throw new Error(d.error || 'HTTP ' + r.status)
        const gate = await pinGate(d.akcia || 'cp_zmazanie_odoslanej', { cp: id, nazov: label || '' })
        if (!gate || !gate.ok) { toast.error('Zrušené — bez PIN-u ponuka ostáva v zozname.'); return }
        r = await fetchAuth('/api/quotes/' + id, { method: 'DELETE', headers: { 'X-Pin-Audit-Id': String(gate.audit_id || '') } })
      }
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'HTTP ' + r.status) }
      toast.success('Zmazané.')
      if (selId === id) { setSelId(null); setEd(null) }
      load()
    } catch (e) { toast.error('Nepodarilo sa: ' + e.message) }
  }
  const openPdf = (id, print) => {
    const t = localStorage.getItem('kamir_token') || ''
    otvorDokument('/api/quotes/' + id + '/pdf?token=' + encodeURIComponent(t) + (print ? '&print=1' : ''), toast)   // p97: najprv uložiť
  }

  // Nová verzia odoslanej CP (ústava 67.14) — rovnaká cesta ako stavebná CP.
  const novaVerzia = async (id) => {
    if (!window.confirm('CP je po odoslaní uzamknutá. Vytvoriť novú verziu na úpravu? Odoslaná verzia ostane archivovaná.')) return
    try {
      const r = await fetchAuth('/api/quotes/' + id + '/aktualizovat', { method: 'POST' })
      const d = await r.json(); if (!r.ok) throw new Error(d.error || 'chyba')
      toast.success('Nová verzia v' + d.version + ' vytvorená — uprav a znova odošli.')
      await reopen(d.id)
    } catch (e) { toast.error('Chyba: ' + e.message) }
  }

  // Odoslanie zákazníkovi — ZDIEĽANÝ dialóg (ústava 103.E), rovnaký ako CP/OBJ/Obhliadky.
  const openSendMail = async (row) => {
    if (!(await pripravDokument(toast))) return   // p97: najprv uložiť, až potom okno odoslania (ústava 67.15)
    const cust = customers.find(c => c.id === row.customerId)
    const custEmail = row.customer_email || (cust && cust.email) || ''
    const custPhone = row.customer_phone || (cust && (cust.phone || cust.contact)) || ''
    setMailFor({
      row,
      to: String(row.contact_email || '').trim() || custEmail,
      cc: String(row.contact_email2 || '').trim(),
      phone: normPhone(String(row.contact_phone || '').trim() || custPhone),
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* KPI — rovnako ako stavebná CP */}
      <KpiRow cols={2}>
        <KpiCard title="Spolu" color={COLORS.nav} value={kpi.total} />
        <KpiCard title="Odoslané" color={COLORS.add} value={kpi.sent} />
      </KpiRow>
      <div style={{ height: SPACE.md }} />

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: SPACE.md, flex: 1, minHeight: 0 }}>
        {/* ĽAVÝ — zoznam */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
          <BtnAdd onClick={startNew}>+ Nová predajňová CP</BtnAdd>
          <SearchInput value={search} onChange={setSearch} placeholder="Hľadať v predajňových CP…" />
          <FilterChips value={filter} onChange={setFilter} options={FILTERS} />
          <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
            {filtered.length === 0 ? <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted, fontSize: TYPO.sizes.sm }}>
              {list.length ? 'Nič nezodpovedá filtru.' : 'Zatiaľ žiadne predajňové CP.'}
            </div> : filtered.map(q => {
              const strip = (q.status === 'sent' || q.status === 'accepted') ? COLORS.add : q.status === 'rejected' ? COLORS.muted : COLORS.remove
              return (
                <ListRow key={q.id} dataId={q.id} selected={selId === q.id} stripColor={strip}
                  onClick={() => openDetail(q)} onDoubleClick={() => openDetail(q)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text, flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>{zakLabel(q)}</span>
                        <StatusTag status={q.status} />
                      </div>
                      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2, fontFamily: TYPO.mono, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {[q.quote_number || q.id, String(q.date_issued || '').slice(0, 10).split('-').reverse().join('.'), q.stavba].filter(Boolean).join(' · ')}
                      </div>
                      {q.sent_at ? <div style={{ fontSize: TYPO.sizes.xs, marginTop: 2, color: COLORS.add, fontWeight: TYPO.weights.semibold }}>E-mail {SK.datumCas(q.sent_at)}</div> : null}
                    </div>
                    <RowMenu actions={[
                      { label: 'Náhľad PDF', onClick: () => openPdf(q.id, false) },
                      { label: 'Tlač', onClick: () => openPdf(q.id, true) },
                      { label: q.sent_at ? 'Odoslať znova e-mailom' : 'Odoslať e-mailom', onClick: () => openSendMail(q) },
                      ...(q.sent_at ? [{ label: 'Aktualizovať (nová verzia)', onClick: () => novaVerzia(q.id) }] : []),
                      { label: 'Duplikovať', icon: '⧉', onClick: () => duplikovat(q) },
                      { label: 'Zmazať', danger: true, onClick: () => delCp(q.id, q.quote_number || q.id) },
                    ]} />
                  </div>
                </ListRow>
              )
            })}
          </div>
          <ListStatusBar>↑↓ výber · Enter otvoriť</ListStatusBar>
        </div>

        {/* PRAVÝ — editor v brand ráme (rovnako ako stavebná CP) */}
        <div style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ height: 4, background: COLORS.brand }} />
          {ed ? (<>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, padding: `${SPACE.sm}px ${SPACE.lg}px`, borderBottom: `1px solid ${COLORS.line}`, background: COLORS.bg, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra, color: COLORS.brandText }}>
                {isNew ? 'Nová predajňová cenová ponuka' : (ed.quote_number || selId)}
              </h2>
              {!isNew && (ed.custName || (customers.find(x => x.id === ed.customerId) || {}).name) && (
                <span style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>
                  {ed.custName || (customers.find(x => x.id === ed.customerId) || {}).name}
                </span>
              )}
              {!isNew && <StatusTag status={ed.status} />}
              {!isNew && ed.doc_version > 1 && (
                <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.nav, background: TINT.nav, borderRadius: RADIUS.sm, padding: '1px 7px' }}>verzia {ed.doc_version}</span>
              )}
              {dirty && <span title="Máš neuložené zmeny" style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.remove, background: TINT.remove || 'rgba(220,38,38,0.10)', borderRadius: RADIUS.sm, padding: '1px 7px' }}>neuložené zmeny</span>}
              <div style={{ flex: 1 }} />
              {!isNew && <Btn variant="ghost" onClick={() => openPdf(selId, false)}>Náhľad PDF</Btn>}
              {!isNew && <Btn variant="ghost" onClick={() => openPdf(selId, true)}>Tlač</Btn>}
              {!isNew && <Btn variant="ghost" onClick={() => { const row = list.find(x => x.id === selId); if (row) openSendMail(row) }}>Poslať zákazníkovi</Btn>}
              {!isNew && docLocked && <Btn variant="ghost" onClick={() => novaVerzia(selId)}>Aktualizovať (nová verzia)</Btn>}
              {!isNew && <Btn variant="ghost" onClick={() => delCp(selId, ed.quote_number || selId)}>Zmazať</Btn>}
              <BtnAdd onClick={save} disabled={saving || docLocked}>{saving ? 'Ukladám…' : (isNew ? 'Vytvoriť CP' : '✓ Uložiť')}</BtnAdd>
            </div>

            {/* Zámok odoslaného dokumentu (ústava 67.14) */}
            {docLocked && (
              <div style={{ padding: `${SPACE.sm}px ${SPACE.lg}px`, background: TINT.add, borderBottom: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
                Odoslané zákazníkovi {SK.datumCas(ed.sent_at)} — obsah je uzamknutý. Na úpravu použi „Aktualizovať (nová verzia)".
              </div>
            )}

            {/* Detail | História — dohľadateľnosť kto/čo/kedy (ústava 12.0/1, Mirko GO 28.7.) */}
            {!isNew && (
              <div style={{ padding: `${SPACE.sm}px ${SPACE.lg}px 0`, borderBottom: `1px solid ${COLORS.line}` }}>
                <Tabs value={tab} onChange={setTab} tabs={[{ key: 'detail', label: 'Detail' }, { key: 'historia', label: 'História' }]} />
              </div>
            )}

            <div style={{ padding: SPACE.md, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
              {tab === 'historia' && !isNew ? (
                <HistoryPanel module="ponuky" entityId={selId} />
              ) : loadingDetail ? <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>Načítavam…</div> : (<>
                {/* PANEL VSTUPNÝCH ÚDAJOV — zdieľaný FieldGrid, identický s CP (Mirko 27.7.) */}
                <FieldGrid>
                  <Field label="Zákazník">
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SearchableSelect value={ed.customerId || ''} allowCreate
                          onChange={v => {
                            const exists = customers.some(c => c.id === v)
                            if (exists) pickCustomer(v)
                            else if (v && v.trim()) { setNewCustomerPrefill(v.trim()); setNewCustomerOpen(true) }
                          }}
                          options={customers.map(c => ({ value: c.id, label: custLabel(c) }))}
                          placeholder="Hľadať podľa mena alebo IČO…" />
                      </div>
                      <button type="button" onClick={() => { setNewCustomerPrefill(''); setNewCustomerOpen(true) }}
                        style={{ padding: '0 12px', border: `1px solid ${COLORS.line}`, background: COLORS.bg, color: COLORS.brand, borderRadius: RADIUS.sm, cursor: 'pointer', fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.sm, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>+ Nový</button>
                    </div>
                    {(() => {
                      const sc = customers.find(c => c.id === ed.customerId)
                      if (!sc) return null
                      const em = sc.email, ph = sc.phone || sc.contact
                      if (!em && !ph) return null
                      return <div style={{ marginTop: 5, fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ opacity: 0.75 }}>Na karte partnera:</span>
                        {em && <span>E-mail: <strong style={{ color: COLORS.text }}>{em}</strong></span>}
                        {ph && <span>Tel: <strong style={{ color: COLORS.text }}>{ph}</strong></span>}
                      </div>
                    })()}
                  </Field>
                  <Field label="Stavba / projekt (voliteľné)">
                    <input style={inp} value={ed.stavba} onChange={e => setF('stavba', e.target.value)} placeholder="objektová cena — nepovinné" />
                  </Field>
                  <Field label="Číslo CP">
                    <input style={{ ...inp, color: COLORS.muted, background: COLORS.bgSoft }} readOnly value={isNew ? '(pridelí sa po uložení)' : (ed.quote_number || '—')} />
                  </Field>
                  <Field label="Dátum vystavenia">
                    <input style={inp} type="date" value={ed.date_issued} onChange={e => setF('date_issued', e.target.value)} />
                  </Field>
                  <Field label="Platnosť (dní)">
                    <input style={inp} type="number" value={ed.validity_days} onChange={e => setF('validity_days', e.target.value)} />
                  </Field>
                  <Field label="Stav">
                    <select style={inp} value={ed.status} onChange={e => setF('status', e.target.value)}>
                      {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label="Pripravil">
                    <input style={inp} value={ed.prepared_by} onChange={e => setF('prepared_by', e.target.value)} />
                  </Field>
                  <Field label="DPH % (dopočíta sa k cenám bez DPH)">
                    <input style={inp} type="number" value={ed.vat_rate} onChange={e => setF('vat_rate', e.target.value)} />
                  </Field>
                  {/* Telefón na doklade — to isté pole ako v stavebnej CP (ústava 103.D),
                      zámok na vstupe podľa ústavy 112. */}
                  <Field label="Telefón na doklade">
                    <TelefonInput style={inp} value={ed.doc_phone || ''} disabled={docLocked}
                      placeholder="prázdne = telefón firmy z Nastavení"
                      onChange={v => setF('doc_phone', v)} />
                  </Field>
                  <Field label="E-mail zákazníka (sem ide ponuka)">
                    <input style={inp} type="email" value={ed.contact_email} placeholder="prazdne = e-mail z karty partnera" onChange={e => setF('contact_email', e.target.value)} />
                  </Field>
                  <Field label="E-mail zákazníka 2 (kópia)">
                    <input style={inp} type="email" value={ed.contact_email2} placeholder="napr. druhy kontakt na stavbe" onChange={e => setF('contact_email2', e.target.value)} />
                  </Field>
                  <Field label="Telefón zákazníka (SMS)">
                    <TelefonInput style={inp} value={ed.contact_phone} placeholder="prázdne = telefón z karty partnera" onChange={v => setF('contact_phone', v)} />
                  </Field>
                </FieldGrid>

                {/* POLOŽKY — tabuľka so zebrou a hlavičkou stĺpcov (ako stavebná CP).
                    ÚSTAVNÉ (Mirko 28.7.): pri KAŽDEJ cene musí byť vidieť, že je BEZ DPH. */}
                <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
                  <div style={{ padding: `${SPACE.sm}px ${SPACE.md}px`, background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}`, display: 'flex', alignItems: 'center', gap: SPACE.md, borderRadius: `${RADIUS.md}px ${RADIUS.md}px 0 0` }}>
                    <strong style={{ fontSize: TYPO.sizes.sm }}>Položky ponuky ({ed.lines.length})</strong>
                    <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>všetky ceny zadávaš BEZ DPH</span>
                    <div style={{ flex: 1 }} />
                    <BtnAdd onClick={addLine}>+ Položka</BtnAdd>
                  </div>
                  <div style={{ ...ROW_GRID, background: COLORS.bgSoft, borderBottom: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase' }}>
                    <span>Položka (PLU)</span><span>Cenová kategória (bez DPH)</span><span>MJ</span>
                    <span style={{ textAlign: 'right' }}>Množstvo</span><span style={{ textAlign: 'right' }}>Cena/MJ bez DPH</span>
                    <span style={{ textAlign: 'right' }}>Spolu bez DPH</span><span />
                  </div>
                  {ed.lines.map((l, idx) => {
                    const maMarzu = (Number(l.nakup) || 0) > 0
                    return (
                    <div key={l._k} style={{ ...ROW_GRID, alignItems: 'start', fontSize: TYPO.sizes.sm, borderBottom: `1px solid ${COLORS.lineSoft || COLORS.line}`, background: idx % 2 ? COLORS.bgSoft : 'transparent' }}>
                      <PluCell line={l} canBuy={canBuy} onPick={r => pickPlu(l._k, r)} onName={v => setLine(l._k, { name: v, plu: '', prices: null, hladina: '', marza: '', nakup: 0, sklad: null })} />
                      <div>
                        <div style={{ display: 'flex', gap: 3 }}>
                          {HLADINY.map(h => {
                            // Hladina bez ceny (VO/Eshop býva v cenníku 0) sa NESMIE dať kliknúť —
                            // predtým ticho nastavila 0 € a vznikla nulová ponuka (audit 28.7.).
                            const maCenu = !!(l.prices && Number(l.prices[h.key]) > 0)
                            return (
                            <button key={h.key} type="button" onClick={() => pickHladina(l._k, h.key)} disabled={!maCenu}
                              title={!l.prices ? 'najprv vyber položku z PLU' : (maCenu ? eur(l.prices[h.key]) + ' bez DPH' : 'táto hladina nie je v cenníku vyplnená')}
                              style={{ flex: 1, padding: '4px 0', fontSize: 10.5, fontWeight: 700, fontFamily: 'inherit', cursor: maCenu ? 'pointer' : 'default', borderRadius: RADIUS.sm, border: `1px solid ${l.hladina === h.key ? COLORS.brand : COLORS.line}`, background: l.hladina === h.key ? COLORS.navBg : '#fff', color: maCenu ? COLORS.text : COLORS.muted, textDecoration: (l.prices && !maCenu) ? 'line-through' : 'none' }}>{h.label}</button>
                            )
                          })}
                          <button type="button" onClick={() => pickHladina(l._k, MARZA_KEY)} disabled={!maMarzu}
                            title={maMarzu ? 'Vlastná marža — cena bez DPH sa dopočíta z nákupnej ceny' : 'vlastná marža sa počíta z nákupnej ceny — vyber položku z PLU'}
                            style={{ flex: 1.2, padding: '4px 0', fontSize: 10.5, fontWeight: 700, fontFamily: 'inherit', cursor: maMarzu ? 'pointer' : 'default', borderRadius: RADIUS.sm, border: `1px solid ${l.hladina === MARZA_KEY ? COLORS.brand : COLORS.line}`, background: l.hladina === MARZA_KEY ? COLORS.navBg : '#fff', color: maMarzu ? COLORS.text : COLORS.muted }}>Vlastná %</button>
                        </div>
                        {l.hladina === MARZA_KEY && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                            <input style={{ ...inp, textAlign: 'right', padding: '3px 6px' }} type="number" step="0.1" value={l.marza}
                              placeholder="marža %" onChange={e => setMarza(l._k, e.target.value)} />
                            <span style={{ fontSize: 11, color: COLORS.muted, whiteSpace: 'nowrap' }}>% z nákupu</span>
                          </div>
                        )}
                      </div>
                      <input style={{ ...inp, textAlign: 'center' }} value={l.unit} onChange={e => setLine(l._k, { unit: e.target.value })} />
                      <input style={{ ...inp, textAlign: 'right' }} type="number" step="0.01" value={l.qty} onChange={e => setLine(l._k, { qty: e.target.value })} />
                      <input style={{ ...inp, textAlign: 'right' }} type="number" step="0.01" value={l.price} title="cena za MJ BEZ DPH"
                        onChange={e => setLine(l._k, { price: e.target.value, hladina: '', marza: '' })} />
                      <span style={{ textAlign: 'right', fontWeight: TYPO.weights.semibold, paddingTop: 6 }}>{eur(lineSum(l))}</span>
                      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/* Poradie položiek (Mirko GO 28.7.) — poradie sa zapíše pri uložení a PDF ho rešpektuje */}
                        <button type="button" onClick={() => moveLine(l._k, -1)} disabled={idx === 0} title="Posunúť vyššie"
                          style={{ border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: RADIUS.sm, cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? COLORS.line : COLORS.muted, padding: '0 4px', height: 26, lineHeight: '24px' }}>↑</button>
                        <button type="button" onClick={() => moveLine(l._k, 1)} disabled={idx === ed.lines.length - 1} title="Posunúť nižšie"
                          style={{ border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: RADIUS.sm, cursor: idx === ed.lines.length - 1 ? 'default' : 'pointer', color: idx === ed.lines.length - 1 ? COLORS.line : COLORS.muted, padding: '0 4px', height: 26, lineHeight: '24px' }}>↓</button>
                        <button type="button" onClick={() => delLine(l._k)} title="Zmazať položku"
                          style={{ border: `1px solid ${COLORS.line}`, background: '#fff', borderRadius: RADIUS.sm, cursor: 'pointer', color: COLORS.muted, padding: '0 5px', height: 26, lineHeight: '24px' }}>✕</button>
                      </div>
                      {canBuy && (Number(l.nakup) || 0) > 0 ? (
                        <span style={{ gridColumn: '1 / -1', fontSize: TYPO.sizes.xs, color: COLORS.muted, textAlign: 'right', paddingBottom: 4 }}>
                          nákup {eur(l.nakup)} bez DPH · marža:{' '}
                          <b style={{ color: (Number(l.price) - l.nakup) >= 0 ? (COLORS.add || '#15803d') : (COLORS.remove || '#dc2626') }}>
                            {eur((Number(l.price) || 0) - (Number(l.nakup) || 0))}/MJ ({n2(((Number(l.price) || 0) / (Number(l.nakup) || 1) - 1) * 100)} %)
                          </b>
                        </span>
                      ) : null}
                    </div>
                    )
                  })}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, padding: `${SPACE.sm}px ${SPACE.md}px`, background: COLORS.bgSoft, fontSize: TYPO.sizes.sm, borderRadius: `0 0 ${RADIUS.md}px ${RADIUS.md}px` }}>
                    <span>Spolu bez DPH: <b>{eur(bez)}</b></span>
                    <span>DPH {vatPct} %: <b>{eur(dph)}</b></span>
                    <span style={{ fontSize: 15 }}>Celkom s DPH: <b>{eur(bez + dph)}</b></span>
                  </div>
                </div>

                {/* Poznámka pre zákazníka — TLAČÍ SA na doklad (quote_pdf: sekcia „Poznámky" = custom_text).
                    Mirko GO 28.7. — doteraz sa dala napísať len interná, ktorá do PDF nejde. */}
                <Field label="Poznámka na doklade — VIDÍ JU ZÁKAZNÍK (tlačí sa v PDF)">
                  <textarea style={{ ...inp, resize: 'vertical', minHeight: 40 }} value={ed.custom_text || ''}
                    placeholder="napr. Tovar je skladom. Ceny platia pri odbere celého množstva."
                    onChange={e => setF('custom_text', e.target.value)} />
                </Field>

                {/* Prepínač „Nezobrazovať celkovú sumu" — rovnaké ako v CP (quote_pdf: hide_total=1 = bez sumára, ceny riadkov ostávajú).
                    Mirko 10.9.2026 (fáza 3, bod 92): v Predajni doteraz nebolo čo zaškrtnúť. Ukladá sa s formulárom. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: docLocked ? 'default' : 'pointer', margin: '2px 0 8px', color: '#334155' }}>
                  <input type="checkbox" checked={Number(ed.hide_total) === 1} disabled={docLocked}
                    onChange={e => setF('hide_total', e.target.checked ? 1 : 0)} />
                  Nezobrazovať celkovú sumu v ponuke (variantné položky — ceny riadkov ostávajú)
                </label>

                {/* Interná poznámka — Mirko 27.7. „interná poznámka musí byť" (do PDF NEJDE) */}
                <Field label="Interná poznámka (NIE v PDF — len pre nás)">
                  <textarea style={{ ...inp, resize: 'vertical', minHeight: 40 }} value={ed.internal_note}
                    onChange={e => setF('internal_note', e.target.value)} />
                </Field>

                <div style={{ display: 'flex', gap: 8 }}>
                  <BtnAdd onClick={save} disabled={saving || docLocked}>{saving ? 'Ukladám…' : (isNew ? 'Vytvoriť CP' : '✓ Uložiť')}</BtnAdd>
                  <Btn variant="ghost" onClick={() => { if (!smiemOdist()) return; setEd(null); setIsNew(false); setSelId(null); setBaseline('') }}>Zrušiť</Btn>
                </div>
              </>)}
            </div>
          </>) : (
            <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>
              Vyber CP vľavo alebo vytvor <b>Novú predajňovú CP</b>.<br />
              Katalóg položiek sa ťahá z PLU cenníka — 3 cenové hladiny (MO / VO / Eshop) + vlastná marža v %{canBuy ? ' z nákupnej ceny' : ''}.
              <br />Všetky ceny sa zadávajú <b>bez DPH</b>.
            </div>
          )}
        </div>
      </div>

      <NoviCustomerDialog
        open={newCustomerOpen}
        prefillName={newCustomerPrefill}
        onClose={() => { setNewCustomerOpen(false); setNewCustomerPrefill('') }}
        onCreated={c => {
          setCustomers(prev => [...prev, c])
          pickCustomer(c.id)
          setNewCustomerOpen(false); setNewCustomerPrefill('')
        }}
      />

      {/* ZDIEĽANÝ dialóg „Poslať zákazníkovi" — ústava 103.E (Mirko: „ideme jeden systém") */}
      {mailFor && (
        <SendCustomerDialog open onClose={() => setMailFor(null)}
          title={`Odoslať predajňovú CP ${mailFor.row.quote_number || mailFor.row.id} zákazníkovi`}
          customerId={mailFor.row.customerId || mailFor.row.customer_id || ''}
          initTo={mailFor.to} initCc={mailFor.cc} phone={mailFor.phone}
          initSubject={`Cenová ponuka ${mailFor.row.quote_number || mailFor.row.id}`}
          initText={`Dobrý deň,\n\nv prílohe Vám posielame cenovú ponuku č. ${mailFor.row.quote_number || mailFor.row.id}.\nUvedené ceny sú bez DPH.\n\nV prípade otázok nás neváhajte kontaktovať.\n\nS pozdravom,\nKAMIR s.r.o.`}
          attachmentName={`CP_${String(mailFor.row.quote_number || mailFor.row.id).replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`}
          attachmentNote="vygeneruje sa automaticky z aktuálneho stavu ponuky."
          footNote={'Po odoslaní sa ponuka uzamkne — úprava už len cez „Aktualizovať (nová verzia)".'}
          smsLabel="SMS, že mu bola odoslaná CP"
          smsText={'Dobry den, prave sme Vam poslali cenovu ponuku ' + (mailFor.row.quote_number || mailFor.row.id) + ' na email. V pripade otazok volajte 0918 443 675. KAMIR'}
          smsEntityType="quote" smsEntityId={mailFor.row.id}
          onSend={async ({ to, cc, subject, text }) => {
            const x = await fetchAuth('/api/quotes/' + mailFor.row.id + '/send-email', { method: 'POST', body: JSON.stringify({ email: to, cc, subject, text }) })
            const d = await x.json(); if (!x.ok) throw new Error(d.error || 'Odoslanie zlyhalo')
            toast.success('Odoslané — CP je odteraz uzamknutá (úprava cez „Aktualizovať").')
            return { to: d.to }
          }}
          onSent={() => { const id = mailFor.row.id; setMailFor(null); reopen(id) }} />
      )}
    </div>
  )
}
